import Pagination from 'components/Blocks/Pagination/Pagination';
import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import { categoryDetailContentMessageTwClass } from 'components/Pages/CategoryDetail/CategoryDetailProductsWrapper';
import { mapListedProductConnectionType } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { BrandProductsQueryDocumentApi } from 'graphql/generated';
import { useProductsData } from 'helpers/loadMore/useProductsData';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useGtmBrandProductListView } from 'hooks/gtm/useGtmBrandProductListView';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { RefObject, useEffect, useState } from 'react';
import { BrandDetailType } from 'types/brand';

type BrandDetailProductsWrapperProps = {
    brand: BrandDetailType;
    paginationScrollTargetRef: RefObject<HTMLDivElement>;
};

export const BrandDetailProductsWrapper: FC<BrandDetailProductsWrapperProps> = ({
    brand,
    paginationScrollTargetRef,
}) => {
    const { t } = useTranslation();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const { asPath } = useRouter();

    const currentPage = useCurrentPageQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const { currencyCode } = useDomainConfig();
    const totalCount = brand.productConnection.totalCount;

    const { products, areProductsFetching, isLoadingMoreProducts } = useProductsData(
        BrandProductsQueryDocumentApi,
        totalCount,
        shouldRefetch,
    );
    const brandProducts = products ? mapListedProductConnectionType(products, currencyCode).products : [];
    useGtmBrandProductListView(brand, getUrlWithoutGetParameters(asPath), brandProducts, areProductsFetching);

    useBroadcastChannel('orderCompleteBroadcast', () => {
        setShouldRefetch(true);
    });

    useEffect(() => {
        if (shouldRefetch) {
            setShouldRefetch(false);
        }
    }, [shouldRefetch]);

    return (
        <>
            {(brandProducts.length !== 0 || areProductsFetching) && (
                <ProductsList
                    currentPage={currentPage}
                    gtmListName="brand"
                    isLoadMore={currentLoadMore > 0}
                    isLoading={areProductsFetching || isLoadingMoreProducts}
                    products={brandProducts}
                    type={ProductListTypes.Normal}
                />
            )}
            {brandProducts.length !== 0 && (
                <Pagination
                    isWithLoadMore
                    isLoading={isLoadingMoreProducts}
                    paginationScrollTargetRef={paginationScrollTargetRef}
                    totalCount={totalCount}
                />
            )}
            {totalCount === 0 && !areProductsFetching && (
                <div className={categoryDetailContentMessageTwClass}>
                    <div>
                        <strong>{t('No results match the filter')}</strong>
                    </div>
                    <div>
                        <Trans components={{ 0: <br /> }} i18nKey="ProductsNoResults" />
                    </div>
                </div>
            )}
        </>
    );
};
